import { FC } from 'react';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { APP, ROUTES } from 'config';
import {
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useSearchParams } from 'utils/navigation';
import {
  adminAdminsFiltersSchema,
  adminStatuses,
  COLOR_BY_STATUS,
  useAdminAdmins,
} from 'store/admin-admins';

export const AdminAdminsList: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();

  const { searchParams: filters, navigate } = useSearchParams(
    adminAdminsFiltersSchema,
  );

  const { data: admins, isLoading } = useAdminAdmins(filters);

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('sidebar.administrators'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'administrators',
                label: t('sidebar.administrators'),
                url: ROUTES.admin.admins.list,
                isActive: path === ROUTES.admin.admins.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('administrators.add_administrator'),
                      color: 'primary',
                      onClick: () => history.push(ROUTES.admin.admins.create),
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="10px"
              >
                <ListTotal total={admins?.total} />

                <Stack direction="row" alignItems="center" gap="10px">
                  <Filters
                    value={filters}
                    initialValues={adminAdminsFiltersSchema.parse({})}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  >
                    {({ value, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('common.status')}
                          value={value.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="status"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple={true}
                              searchable={false}
                              options={adminStatuses.map((status) => ({
                                key: status,
                                label: t(`status.${status}`),
                              }))}
                            />
                          )}
                        </FilterButton>

                        <OpenSearchFilter
                          value={filters.name}
                          onApplyFilter={(v) => setFilter('name', v)}
                          placeholder={t('common.search')}
                        />
                      </>
                    )}
                  </Filters>
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={admins?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'name',
                header: t('common.name'),
                cell: ({ row: { original: admin } }) => (
                  <Link
                    label={admin.fullName}
                    to={ROUTES.admin.admins.details.replace(':id', admin.id)}
                  />
                ),
              },
              {
                id: 'email',
                header: t('common.email'),
                cell: ({ row: { original: admin } }) => (
                  <Link
                    label={admin.email}
                    to={ROUTES.admin.admins.details.replace(':id', admin.id)}
                  />
                ),
              },
              {
                id: 'phone',
                header: t('client.admin_phone'),
                cell: ({ row: { original: admin } }) => admin.phone,
              },
              {
                id: 'status',
                header: t('common.status'),
                cell: ({ row: { original: admin } }) => (
                  <Typography color={COLOR_BY_STATUS[admin.status]}>
                    {t(`status.${admin.status}`)}
                  </Typography>
                ),
              },
            ]}
            sort={{
              value: filters.sort,
              onChange: (v) => navigate({ params: { sort: v } }),
            }}
            pagination={
              admins && {
                count: admins.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
